import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';

import { getCookieDomain } from 'utils/url';

import commonEn from './en/common.json';
import countriesregionsEn from './en/countriesregions.json';
import errorsEn from './en/errors.json';
import headersEn from './en/headers.json';
import pagesEn from './en/pages.json';

const resources = {
  en: { common: commonEn, countriesregions: countriesregionsEn, errors: errorsEn, headers: headersEn, pages: pagesEn },
};

const lazyLanguages = ['es', 'ru', 'vi', 'zh'];

// Only languages we ship have a folder; everything else ('dev', 'en-US', detector leftovers)
// resolves to an empty bundle so i18next never logs a failed dynamic import.
const loadResource = (language: string, namespace: string) =>
  lazyLanguages.includes(language) ? import(`./${language}/${namespace}.json`) : Promise.resolve({});

const referrerDetector = {
  name: 'referrer',
  lookup() {
    try {
      const { pathname } = new URL(document.referrer);
      const segment = pathname.split('/')[1];

      return segment || undefined;
    } catch {
      return undefined;
    }
  },
};

const languageDetector = new LanguageDetector();
languageDetector.addDetector(referrerDetector);

const supportedLngs = ['en', 'es', 'vi', 'ru', 'zh'];

if (process.env.REACT_APP_ENV !== 'production') {
  supportedLngs.push('dev');
}

export const i18nReady = i18n
  .use(languageDetector)
  .use(initReactI18next)
  .use(resourcesToBackend(loadResource))
  .init({
    resources,
    partialBundledLanguages: true,
    detection: {
      order: ['cookie', 'referrer', 'localStorage', 'navigator'],
      lookupCookie: 'NEXT_LOCALE',
      lookupLocalStorage: 'language',
      caches: ['cookie'],
      cookieOptions: { domain: getCookieDomain(), path: '/' },
      convertDetectedLanguage: (lng) => {
        const languageMap = {
          cn: 'zh',
          es: 'es',
          ru: 'ru',
          ua: 'en',
          vi: 'vi',
          ko: 'en',
          jp: 'en',
          pt: 'en',
          ar: 'en',
        };

        return languageMap[lng as keyof typeof languageMap] ?? lng;
      },
    },
    returnEmptyString: process.env.REACT_APP_ENV === 'development',
    fallbackLng: process.env.REACT_APP_ENV !== 'production' ? 'dev' : 'en',
    defaultNS: 'pages',
    ns: ['common', 'countriesregions', 'errors', 'headers', 'pages'],
    supportedLngs,
    load: process.env.REACT_APP_ENV !== 'production' ? 'all' : 'languageOnly',
    debug: process.env.REACT_APP_ENV === 'development',
  });

export default i18n;
